import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import type { ExamWeakArea } from "@/lib/exam-api";

/**
 * apps/web :: components/exam :: ExamWeakAreasCallouts
 *
 * ROADS-430 — renders one callout per "weak area" returned by the
 * backend diagnostic, deep-linking to the filtered TrainingPage.
 *
 * Callout copy:
 *   "You scored 50% on right-of-way (2 of 4). Revisit the right-of-way
 *    lessons."
 *   + a "Review right-of-way lessons" link → /training?category=RIGHT_OF_WAY.
 *
 * The link target uses the lower-case kebab form
 * (`right-of-way`) per ROADS-430 ticket text, even though the wire
 * uses UPPER_SNAKE. We translate at render time so the URL matches the
 * legible form the ticket asked for ("/training?category=right-of-way").
 *
 * Empty state: when [weakAreas] is empty AND [showEmptyState] is true
 * we render an encouraging "no weak areas" line. The default for
 * [showEmptyState] is false because the typical caller (ExamResultsCard)
 * suppresses the whole section when the list is empty.
 *
 * A11y:
 *   - Outer `<section>` has an `aria-labelledby` to the visible heading.
 *   - Each callout is its own `<article>` with role=region for SR
 *     navigation.
 *   - Action link is a real `<a>` (Link), not a div-with-onClick.
 */
export interface ExamWeakAreasCalloutsProps {
  weakAreas: ExamWeakArea[];
  /** Render the "No weak areas detected" line when the list is empty. */
  showEmptyState?: boolean;
}

export default function ExamWeakAreasCallouts({
  weakAreas,
  showEmptyState = false,
}: ExamWeakAreasCalloutsProps) {
  const { t } = useTranslation();

  if (weakAreas.length === 0) {
    if (!showEmptyState) return null;
    return (
      <p
        data-testid="exam-weakareas-empty"
        className="text-sm text-neutral-700"
      >
        {t("exam.results.weakAreasNone")}
      </p>
    );
  }

  return (
    <section
      data-testid="exam-weakareas"
      aria-labelledby="exam-weakareas-heading"
      className="rounded-lg border border-danger/30 bg-danger/5 p-md sm:p-lg"
    >
      <h3
        id="exam-weakareas-heading"
        className="font-display text-lg font-bold text-brand-primary"
      >
        {t("exam.results.weakAreasHeading")}
      </h3>
      <p className="mt-xs text-sm text-neutral-800">
        {t("exam.results.weakAreasIntro")}
      </p>
      <ul className="mt-md flex flex-col gap-sm">
        {weakAreas.map((wa) => {
          const percent = Math.round(wa.accuracy * 100);
          const categoryLabel =
            t(`exam.results.sectionLabels.${wa.category}`, {
              defaultValue: wa.category,
            }) ?? wa.category;
          // Server returns UPPER_SNAKE; URL slug per ROADS-430 is
          // lower kebab-case for human readability.
          const slug = wa.category.toLowerCase().replace(/_/g, "-");
          return (
            <li
              key={wa.category}
              data-testid={`exam-weakareas-callout-${wa.category}`}
              data-category={wa.category}
              className="rounded-md border border-danger/20 bg-white p-sm shadow-sm"
            >
              <p className="text-sm text-neutral-900">
                {t("exam.results.weakAreasCallout", {
                  category: categoryLabel,
                  percent,
                  correct: wa.correct,
                  answered: wa.answered,
                })}
              </p>
              <Link
                to={`/training?category=${slug}`}
                data-testid={`exam-weakareas-link-${wa.category}`}
                className="mt-xs inline-flex items-center text-sm font-semibold text-brand-primary underline underline-offset-2 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
              >
                {t("exam.results.weakAreasReviewLink", {
                  category: categoryLabel,
                })}
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
